import React, { useState } from 'react';
import { IonToggle } from '@ionic/react';
import CatalogoSection from './CatalogoSection';
import { bagHandleOutline } from 'ionicons/icons';
import { categoriaReferencia, normalizarCategoriasReferenciadas, normalizarOfertasCarrinho, OfertasCarrinho, resumoOfertasCarrinho } from './ofertasCarrinho';
import './OfertasCarrinhoEditor.css';

type Props = {
  value: OfertasCarrinho;
  categorias: any[];
  produtos: any[];
  origemId?: string;
  disabled?: boolean;
  onChange: (value: OfertasCarrinho) => void;
  children?: React.ReactNode;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
};

const textoBusca = (value: any) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR');

const OfertasCarrinhoEditor: React.FC<Props> = ({ value, categorias, produtos, origemId, disabled = false, onChange, children, expanded, onExpandedChange }) => {
  const [busca, setBusca] = useState('');
  const regra = normalizarOfertasCarrinho(value);
  regra.categoryIds = normalizarCategoriasReferenciadas(regra.categoryIds, categorias);
  const atualizar = (campos: Partial<OfertasCarrinho>) => onChange({ ...regra, ...campos });
  const referencias = regra.type === 'CATEGORY' ? regra.categoryIds : regra.productIds;
  const trocarReferencia = (ref: string, checked: boolean) => {
    const campo = regra.type === 'CATEGORY' ? 'categoryIds' : 'productIds';
    atualizar({ [campo]: checked ? Array.from(new Set([...referencias, ref])) : referencias.filter(id => id !== ref) });
  };
  const termosBusca = textoBusca(busca).trim().split(/\s+/).filter(Boolean);
  const opcoes = (regra.type === 'CATEGORY' ? categorias.filter(cat => cat.tipo !== 'complementos')
    : produtos.filter(p => p.id && p.tipo !== 'complementos' && p.id !== origemId))
    .filter(item => {
      const texto = textoBusca(regra.type === 'CATEGORY' ? item.nome : `${item.nome || ''} ${item.codigo || ''}`);
      return termosBusca.every(termo => texto.includes(termo));
    });

  return <CatalogoSection title="Ofertas no carrinho" description="Sugira produtos complementares quando este item estiver no pedido."
    icon={bagHandleOutline} iconColor="#15803d" iconBackground="#edf8ef" className="catalogo-ofertas-card" expanded={expanded} onExpandedChange={onExpandedChange}>
      <fieldset className="catalogo-ofertas-controls" disabled={disabled}>
        <div className="catalogo-toggle-row">
          <div style={{ flex: 1 }}>
            <div className="catalogo-toggle-label">Ativar ofertas no carrinho</div>
            <div className="catalogo-toggle-desc">Exibir sugestões de produtos complementares no carrinho</div>
          </div>
          <IonToggle aria-label="Ativar ofertas no carrinho" checked={regra.enabled} disabled={disabled}
            onIonChange={event => atualizar({ enabled: event.detail.checked })} />
        </div>
        <fieldset className="catalogo-ofertas-types">
          <legend>Quero oferecer</legend>
          <label><input type="radio" name="tipo-oferta" checked={regra.type === 'CATEGORY'} onChange={() => { atualizar({ type: 'CATEGORY', productIds: [] }); setBusca(''); }} /> Categorias</label>
          <label><input type="radio" name="tipo-oferta" checked={regra.type === 'PRODUCT'} onChange={() => { atualizar({ type: 'PRODUCT', categoryIds: [] }); setBusca(''); }} /> Produtos específicos</label>
        </fieldset>
        <label className="catalogo-ofertas-field">
          <span>Buscar {regra.type === 'CATEGORY' ? 'categorias' : 'produtos'}</span>
          <input type="search" placeholder={regra.type === 'CATEGORY' ? 'Nome da categoria' : 'Nome ou código do produto'} value={busca} onChange={event => setBusca(event.target.value)} />
        </label>
        <div className="catalogo-ofertas-options">
          {opcoes.map(item => {
            const ref = regra.type === 'CATEGORY' ? categoriaReferencia(item) : item.id;
            return <label className="catalogo-ofertas-check" key={ref}>
              <input type="checkbox" checked={referencias.includes(ref)} onChange={event => trocarReferencia(ref, event.target.checked)} />
              <span>{item.nome}{regra.type === 'PRODUCT' && !item.internet ? ' (fora do catálogo digital)' : ''}</span>
            </label>;
          })}
          {!opcoes.length && <p role="status">{busca ? 'Nenhum resultado encontrado.' : 'Nenhuma opção disponível no catálogo.'}</p>}
        </div>
        {regra.type === 'PRODUCT' && <p className="catalogo-ofertas-help">Produtos fora do catálogo digital podem ser configurados, mas só serão sugeridos quando estiverem visíveis e disponíveis.</p>}
        <label className="catalogo-ofertas-field">
          <span>Quantidade máxima de produtos</span>
          <select value={regra.maxItems} onChange={event => atualizar({ maxItems: Number(event.target.value) })}>
            {[1, 2, 3].map(quantidade => <option key={quantidade} value={quantidade}>{quantidade}</option>)}
          </select>
        </label>
        {regra.type === 'CATEGORY' && <>
          <p className="catalogo-ofertas-help">Os produtos serão escolhidos na ordem do catálogo.</p>
          <label className="catalogo-ofertas-check">
            <input type="checkbox" checked={regra.skipCategoryIfAlreadyInCart} onChange={event => atualizar({ skipCategoryIfAlreadyInCart: event.target.checked })} />
            <span>Não sugerir esta categoria se já existir um produto dela no carrinho</span>
          </label>
        </>}
      </fieldset>
      <div className="catalogo-ofertas-summary" role="status">
        {referencias.length ? <>
          <p className="catalogo-ofertas-summary-targets">Oferecer: {resumoOfertasCarrinho(regra, categorias, produtos)}</p>
          <p>Até {regra.maxItems} {regra.maxItems === 1 ? 'produto' : 'produtos'}{!regra.enabled ? ' • Ofertas desativadas' : ''}</p>
        </> : <p>Nenhuma oferta configurada. Selecione categorias ou produtos acima.</p>}
      </div>
      {children}
      {/* <p className="catalogo-ofertas-help">As alterações serão salvas junto com o produto.</p> */}
  </CatalogoSection>;
};

export default OfertasCarrinhoEditor;
